import {
  Metadata,
  MetadataData,
} from "@metaplex-foundation/mpl-token-metadata";
import { BN, web3 } from "@project-serum/anchor";
import { encode } from "@project-serum/anchor/dist/cjs/utils/bytes/bs58";
import { getGovernance, Vote, withCastVote } from "@solana/spl-governance";
import { NATIVE_MINT, TOKEN_PROGRAM_ID } from "@solana/spl-token";
import { AnchorWallet } from "@solana/wallet-adapter-react";
import {
  AccountMeta,
  ComputeBudgetProgram,
  Keypair,
  PublicKey,
  SystemProgram,
  SYSVAR_RENT_PUBKEY,
  Transaction,
  TransactionInstruction,
} from "@solana/web3.js";
import { saveCancellationReason } from "../../../api/proposals.api";
import {
  CHAIN_ID,
  SPL_PROGRAM_VERSION,
  TREASURY_INDEX,
} from "../../../common/constants/common.constants";
import {
  SOLSEA_ESCROW_ACCOUNT_SIZE,
  SOLSEA_ESCROW_RENT,
} from "../../../common/constants/solsea.constants";
import { ClubType, SequenceType } from "../../../common/enums/club.enum";
import { ClubAction } from "../../../common/enums/common.enums";
import {
  ProposalAction,
  ProposalTypeForProgram,
} from "../../../common/enums/proposals.enums";
import { ICancellationReasonDto } from "../../../common/interfaces/club-application.interface";
import {
  IClubTreasury,
  ISellPermissionConfig,
} from "../../../common/interfaces/clubs.interface";
import { IInstructionSet } from "../../../common/interfaces/common.interface";
import { IProposalWithMetaData } from "../../../common/interfaces/proposals.interface";
import { getTreasuryCount } from "../../../utilities/helpers";
import {
  distributeProfitFromProposal,
  updateVoterWeight,
} from "../../methods/proposals";
import { sendTransaction } from "../../transactions/sendTransaction";
import { sendTransactions } from "../../transactions/sendTransactions";
import {
  acceptOfferSeed,
  accountGovernanceSeed,
  escrowSeed,
  ESCRROW_PROGRAM,
  governanceSeed,
  maxVoterWeightSeed,
  mint,
  offerSeed,
  profitSeed,
  programFactory,
  proposalMetadataSeed,
  realmConfigSeed,
  RPC_CONNECTION,
  solseaProfitAccount,
  solseaProgram,
  splGovernanceProgram,
  treasurySeed,
  unqClubMemberSeed,
  unqClubSeed,
  vaultSeed,
} from "../utilities";
import { createTokenAccountOwnedBySpecifiedAccount } from "./common.helpers";
import { getRemainingAccountForMemberOfNFTClub } from "./nftClubs.helpers";
import { getAccountGovernanceForTradingProposal } from "./proposals.helpers";

export async function createSolseaProposal(
  realmName: string,
  clubTreasury: IClubTreasury,
  payer: AnchorWallet,
  clubType: ClubType,
  name: string,
  description: string,
  action: ProposalAction,
  nftMint: PublicKey,
  offeredAmount: number,
  wantedAmount: number,
  discussionLink: string,
  userRole: string,
  sellerFeeBps: number,
  communityTokenMint: PublicKey,
  sellPermissions?: ISellPermissionConfig[],
  nftMetadata?: PublicKey,
  nftTokenAccount?: PublicKey,
  membersNftTokenAccount?: PublicKey
) {
  try {
    const clubProgram = programFactory();

    const countBuffer = Buffer.alloc(4);
    countBuffer.writeInt32LE(clubTreasury.fundraiseCount, 0);
    const remainingAccounts: AccountMeta[] = [];
    const allInstructions: IInstructionSet[] = [];

    let makerWantedTokenAccount: PublicKey;
    let makerOfferedTokensAccount: PublicKey;
    let offeredTokenMint: PublicKey;
    let wantedTokenMint: PublicKey;

    const optionIndex = 0;
    const transactionIndex = 0;
    const optionIndexBuffer = Buffer.alloc(1);
    optionIndexBuffer.writeUInt8(optionIndex, 0);
    const instructionIndexBuffer = Buffer.alloc(2);
    instructionIndexBuffer.writeInt16LE(transactionIndex, 0);

    const [realmPda] = await PublicKey.findProgramAddress(
      [governanceSeed, Buffer.from(realmName)],
      splGovernanceProgram
    );

    const [clubData] = await PublicKey.findProgramAddress(
      [unqClubSeed, realmPda.toBuffer()],
      clubProgram.programId
    );

    const [vaultPda] = await PublicKey.findProgramAddress(
      [
        unqClubSeed,
        clubData.toBuffer(),
        clubTreasury.treasury.toBuffer(),
        vaultSeed,
        Buffer.from(CHAIN_ID),
      ],
      clubProgram.programId
    );

    //Token account for treasury with native mint
    const { tokenAccount, createAccountIx, createInitAccountInstruction } =
      await createTokenAccountOwnedBySpecifiedAccount(
        payer.publicKey,
        clubTreasury.treasury,
        NATIVE_MINT
      );

    const [defaultAccountGovernancePda] = await PublicKey.findProgramAddress(
      [
        accountGovernanceSeed,
        realmPda.toBuffer(),
        clubTreasury.treasury.toBuffer(),
      ],
      splGovernanceProgram
    );

    const denomMint =
      clubTreasury.denominatedTokenInfo?.mint.toString() ??
      NATIVE_MINT.toString();

    if (action === ProposalAction.BuyNft) {
      makerOfferedTokensAccount = tokenAccount.publicKey;
      const vaultTokenAccount = await createTokenAccountOwnedBySpecifiedAccount(
        payer.publicKey,
        vaultPda,
        nftMint
      );

      makerWantedTokenAccount = vaultTokenAccount.tokenAccount.publicKey;
      wantedTokenMint = nftMint;

      offeredTokenMint = NATIVE_MINT;

      allInstructions.push({
        instructions: [
          vaultTokenAccount.createAccountIx,
          vaultTokenAccount.createInitAccountInstruction,
        ],
        partialSigner: vaultTokenAccount.tokenAccount,
        description: "Create token account owned by vault",
      });
    } else {
      makerOfferedTokensAccount = nftTokenAccount!;
      makerWantedTokenAccount = tokenAccount.publicKey;
      offeredTokenMint = nftMint;
      wantedTokenMint = NATIVE_MINT;
    }

    const {
      accountGovernancePda,
      useDeny,
      isSellPermissionFromZeroAndUserIsFounder,
    } = await getAccountGovernanceForTradingProposal(
      sellPermissions,
      action === ProposalAction.BuyNft ? offeredAmount : wantedAmount,
      realmPda,
      clubProgram,
      clubTreasury,
      defaultAccountGovernancePda,
      userRole,
      action === ProposalAction.BuyNft
        ? offeredTokenMint.toString() === denomMint
        : wantedTokenMint.toString() === denomMint
    );

    const accountGovernance = await getGovernance(
      RPC_CONNECTION,
      accountGovernancePda
    );

    const proposalIndexBuffer = Buffer.alloc(4);
    proposalIndexBuffer.writeInt32LE(
      accountGovernance.account.proposalCount,
      0
    );
    const [proposalAddress] = await PublicKey.findProgramAddress(
      [
        governanceSeed,
        accountGovernancePda.toBuffer(),
        mint.toBuffer(),
        proposalIndexBuffer,
      ],
      splGovernanceProgram
    );

    const [instructionAddress] = await PublicKey.findProgramAddress(
      [
        governanceSeed,
        proposalAddress.toBuffer(),
        optionIndexBuffer,
        instructionIndexBuffer,
      ],
      splGovernanceProgram
    );

    const [offer] = await PublicKey.findProgramAddress(
      [offerSeed, proposalAddress.toBuffer()],
      ESCRROW_PROGRAM
    );

    const [tokenOwnerRecord] = await PublicKey.findProgramAddress(
      [
        governanceSeed,
        realmPda.toBuffer(),
        mint.toBuffer(),
        payer.publicKey.toBuffer(),
      ],
      splGovernanceProgram
    );

    const [realmConfigPda] = await PublicKey.findProgramAddress(
      [realmConfigSeed, realmPda.toBuffer()],
      splGovernanceProgram
    );

    const [proposalMetadata] = await PublicKey.findProgramAddress(
      [unqClubSeed, proposalAddress.toBuffer(), proposalMetadataSeed],
      clubProgram.programId
    );

    const [memberPDA] = await PublicKey.findProgramAddress(
      [
        unqClubSeed,
        clubData.toBuffer(),
        unqClubMemberSeed,
        payer.publicKey.toBuffer(),
      ],
      clubProgram.programId
    );

    const [escrowedWantedTokenAccount] = await PublicKey.findProgramAddress(
      [escrowSeed, offer.toBuffer(), acceptOfferSeed],
      ESCRROW_PROGRAM
    );

    try {
      const { updateVW, voterWeightAddress } = await updateVoterWeight(
        realmPda,
        payer,
        clubTreasury.treasury,
        clubTreasury.fundraiseCount,
        ClubAction.CreateP2PProposal,
        clubType,
        proposalAddress,
        isSellPermissionFromZeroAndUserIsFounder
      );

      const createProposalMetadataInstruction =
        clubProgram.instruction.createProposalMetadata(
          action === ProposalAction.BuyNft
            ? ProposalTypeForProgram.BuySolsea
            : ProposalTypeForProgram.SellSolsea,
          null,
          null,
          null,
          {
            accounts: {
              realm: realmPda,
              clubData,
              proposal: proposalAddress,
              proposalMetadata,
              memberData: memberPDA,
              governance: accountGovernancePda,
              treasuryData: clubTreasury.treasuryDataAddress,
              payer: payer.publicKey,
              systemProgram: web3.SystemProgram.programId,
            },
          }
        );

      const updateProposalDescriptionIx =
        clubProgram.instruction.updateProposalDescription(
          description,
          name,
          ["option YES"],
          discussionLink,
          {
            accounts: {
              proposal: proposalAddress,
              proposalMetadata,
              payer: payer.publicKey,
            },
          }
        );
      const createSolseaProposalIx =
        clubProgram.instruction.createSolseaProposal(
          TREASURY_INDEX + 1,
          CHAIN_ID,
          new BN(offeredAmount),
          new BN(wantedAmount),
          sellerFeeBps,
          useDeny,
          action,
          {
            accounts: {
              clubData: clubData,
              communityTokenMint,
              escrowProgram: ESCRROW_PROGRAM,
              escrowWantedToken: escrowedWantedTokenAccount,
              governance: accountGovernancePda,
              makerWantedToken: makerWantedTokenAccount,
              memberData: memberPDA,
              offer,
              offeredTokenMint,
              wantedTokenMint,
              payer: payer.publicKey,
              proposal: proposalAddress,
              proposalMetadata: proposalMetadata,
              proposalTransaction: instructionAddress,
              realm: realmPda,
              realmConfig: realmConfigPda,
              treasury: clubTreasury.treasury,
              voterWeightRecord: voterWeightAddress,
              treasuryData: clubTreasury.treasuryDataAddress,
              tokenOwnerRecord: tokenOwnerRecord,
              splGovernance: splGovernanceProgram,
              rent: SYSVAR_RENT_PUBKEY,
              systemProgram: SystemProgram.programId,
              tokenProgram: TOKEN_PROGRAM_ID,
            },
          }
        );
      allInstructions.push({
        instructions: [createAccountIx, createInitAccountInstruction],
        partialSigner: tokenAccount,
        description: "Create token account",
      });

      allInstructions.push({
        instructions: [createProposalMetadataInstruction],
        description: "Create proposal metadata",
      });

      allInstructions.push({
        instructions: [updateProposalDescriptionIx],
        description: "Add proposal description",
      });

      allInstructions.push({
        instructions: [updateVW, createSolseaProposalIx],
        description: "Create proposal account, update voter weight for user",
      });

      //TODO@milica-monke-merge: nftMetadata && membersNftTokenAccount only for monke
      if (
        isSellPermissionFromZeroAndUserIsFounder &&
        nftMetadata &&
        membersNftTokenAccount
      ) {
        const castVoteIx: TransactionInstruction[] = [];

        const { updateVW, voterWeightAddress } = await updateVoterWeight(
          realmPda,
          payer,
          clubTreasury.treasury,
          clubTreasury.fundraiseCount,
          ClubAction.CastVote,
          clubType,
          proposalAddress,
          isSellPermissionFromZeroAndUserIsFounder
        );

        castVoteIx.push(updateVW);
        const [maxVoterWeightPda] = await PublicKey.findProgramAddress(
          [unqClubSeed, proposalAddress.toBuffer(), maxVoterWeightSeed],
          clubProgram.programId
        );

        await withCastVote(
          castVoteIx,
          splGovernanceProgram,
          SPL_PROGRAM_VERSION,
          realmPda,
          accountGovernancePda,
          proposalAddress,
          tokenOwnerRecord,
          tokenOwnerRecord,
          payer.publicKey,
          mint,
          Vote.fromYesNoVote(0),
          payer.publicKey,
          voterWeightAddress,
          maxVoterWeightPda
        );

        allInstructions.push({
          instructions: [...castVoteIx],
          description: "Cast vote",
        });
      }

      await sendTransactions(
        RPC_CONNECTION,
        payer,
        allInstructions,
        SequenceType.Sequential,
        isSellPermissionFromZeroAndUserIsFounder
      );
      return proposalAddress;
    } catch (error) {
      console.log(error);
      throw error;
    }
  } catch (error) {
    console.log(error);
    throw error;
  }
}

export async function executeSolseaProposal(
  action: ProposalAction,
  realmName: string,
  wallet: AnchorWallet,
  proposal: IProposalWithMetaData,
  solseaEscrowAccount?: PublicKey,
  seller?: PublicKey
) {
  try {
    const clubProgram = programFactory();
    const allInstructions: IInstructionSet[] = [];
    const [realmAddress] = await PublicKey.findProgramAddress(
      [governanceSeed, Buffer.from(realmName)],
      splGovernanceProgram
    );
    const [clubData] = await PublicKey.findProgramAddress(
      [unqClubSeed, realmAddress.toBuffer()],
      clubProgram.programId
    );
    const [treasury, trBump] = await PublicKey.findProgramAddress(
      [
        unqClubSeed,
        clubData.toBuffer(),
        treasurySeed,
        getTreasuryCount(TREASURY_INDEX),
      ],
      clubProgram.programId
    );

    const [vaultAddress] = await PublicKey.findProgramAddress(
      [
        unqClubSeed,
        clubData.toBuffer(),
        treasury.toBuffer(),
        vaultSeed,
        Buffer.from(CHAIN_ID),
      ],
      clubProgram.programId
    );

    const [memberAddress] = await PublicKey.findProgramAddress(
      [
        unqClubSeed,
        clubData.toBuffer(),
        unqClubMemberSeed,
        wallet.publicKey.toBuffer(),
      ],
      clubProgram.programId
    );

    let solseaEscrow = solseaEscrowAccount;
    let solseaTokens: PublicKey | undefined;

    const requestComputeUnitsTx = ComputeBudgetProgram.requestUnits({
      additionalFee: 0,
      units: 14000000,
    });

    if (action === ProposalAction.SellNft) {
      const solseaEscrowAccount = Keypair.generate();
      const createAccountIx = SystemProgram.createAccount({
        fromPubkey: wallet.publicKey,
        newAccountPubkey: solseaEscrowAccount.publicKey,
        space: SOLSEA_ESCROW_ACCOUNT_SIZE,
        programId: solseaProgram,
        lamports: SOLSEA_ESCROW_RENT,
      });
      solseaEscrow = solseaEscrowAccount.publicKey;
      allInstructions.push({
        description: "Creating SolSea escrow account",
        instructions: [createAccountIx],
        partialSigner: solseaEscrowAccount,
      });
    }
    const [solseaAuthority, authorityBump] = await PublicKey.findProgramAddress(
      [solseaEscrow!.toBuffer()],
      solseaProgram
    );
    let sellerFeeBps: number | null = null;
    let creatorsCount: number | null = null;
    let royalties: number[] | null = null;
    const nftMetadata = await Metadata.getPDA(
      action === ProposalAction.BuyNft
        ? proposal.metadata.wantedTokenMint
        : proposal.metadata.offeredTokenMint
    );

    const rawMetadataAccount = await RPC_CONNECTION.getAccountInfo(nftMetadata);

    if (!rawMetadataAccount) throw new Error("Failed to get nft metadata");
    const metadataAccount: MetadataData = MetadataData.deserialize(
      rawMetadataAccount.data
    );

    if (action === ProposalAction.SellNft) {
      royalties = [];
      const createSolseaTokenAccountIx =
        await createTokenAccountOwnedBySpecifiedAccount(
          wallet.publicKey,
          solseaAuthority,
          proposal.metadata.offeredTokenMint
        );
      allInstructions.push({
        description: "Creating SolSea token account",
        instructions: [
          createSolseaTokenAccountIx.createAccountIx,
          createSolseaTokenAccountIx.createInitAccountInstruction,
        ],
        partialSigner: createSolseaTokenAccountIx.tokenAccount,
      });
      solseaTokens = createSolseaTokenAccountIx.tokenAccount.publicKey;
      sellerFeeBps = metadataAccount.data.sellerFeeBasisPoints / 100;
      creatorsCount = metadataAccount.data.creators?.length ?? 0;
      for (let i = 0; i < 5; i++) {
        if (metadataAccount.data.creators && metadataAccount.data.creators[i]) {
          royalties!.push(metadataAccount.data.creators[i].share);
        } else royalties!.push(0);
      }
    } else {
      solseaTokens = (
        await RPC_CONNECTION.getParsedTokenAccountsByOwner(solseaAuthority, {
          mint: proposal.metadata.wantedTokenMint,
        })
      ).value.find(
        (v) => v.account.data.parsed.info.tokenAmount.uiAmount === 1
      )?.pubkey;
    }
    const solseaRemainingAccounts: AccountMeta[] = [];
    if (action === ProposalAction.BuyNft) {
      solseaRemainingAccounts.push({
        isSigner: false,
        isWritable: true,
        pubkey: seller!,
      });
    }
    metadataAccount.data.creators?.forEach((c) => {
      solseaRemainingAccounts.push({
        isSigner: false,
        isWritable: true,
        pubkey: new PublicKey(c.address),
      });
    });
    const [escrowWantedToken] = await PublicKey.findProgramAddress(
      [escrowSeed, proposal.metadata.offer.toBuffer(), acceptOfferSeed],
      ESCRROW_PROGRAM
    );

    const [profit] = await PublicKey.findProgramAddress(
      [unqClubSeed, treasury.toBuffer(), profitSeed],
      clubProgram.programId
    );

    const [owner, mint, balance] =
      action === ProposalAction.BuyNft
        ? [treasury, NATIVE_MINT, 0]
        : [vaultAddress, proposal.metadata.offeredTokenMint, 1];
    const makerOfferedTokens = (
      await RPC_CONNECTION.getParsedTokenAccountsByOwner(owner, { mint })
    ).value.find(
      (v) => v.account.data.parsed.info.tokenAmount.uiAmount >= balance
    );

    const executeSolseaProposalIx =
      clubProgram.instruction.executeSolseaTransaction(
        action,
        TREASURY_INDEX + 1,
        CHAIN_ID,
        authorityBump,
        creatorsCount,
        sellerFeeBps,
        royalties,
        {
          accounts: {
            clubData,
            offer: proposal.metadata.offer,
            solseaEscrow: solseaEscrow!,
            authorityAccount: solseaAuthority,
            solseaProfitAccount: solseaProfitAccount,
            solseaEscrowTokens: solseaTokens!,
            profitAccount: profit,
            proposalMetadata:
              proposal.proposalBasicData.proposalMetadata.proposalMetadata,
            treasury,
            maker: action === ProposalAction.BuyNft ? treasury : vaultAddress,
            proposal: proposal.proposalBasicData.proposalAccount.pubkey,
            proposalTransaction: proposal.proposalTransactionInfo?.pubkey!,
            memberData: memberAddress,
            solseaProgram,
            wantedTokenMint: proposal.metadata.wantedTokenMint,
            offeredTokenMint: proposal.metadata.offeredTokenMint,
            escrowWantedToken,
            makerWantedToken: proposal.metadata.makerWantedTokenAccount,
            governance:
              proposal.proposalBasicData.proposalAccount.account.governance,
            payer: wallet.publicKey,
            splGovernance: splGovernanceProgram,
            escrowProgram: ESCRROW_PROGRAM,
            systemProgram: SystemProgram.programId,
            tokenProgram: TOKEN_PROGRAM_ID,
            rent: SYSVAR_RENT_PUBKEY,
            makerOfferedToken: makerOfferedTokens?.pubkey!,
          },
          remainingAccounts: solseaRemainingAccounts,
        }
      );
    allInstructions.push({
      description: "Executing proposal",
      instructions: [requestComputeUnitsTx, executeSolseaProposalIx],
    });
    if (action === ProposalAction.BuyNft) {
      const distributeIx = await distributeProfitFromProposal(
        proposal.proposalBasicData,
        wallet,
        treasury,
        realmName,
        action,
        proposal.governanceAccount,
        wallet,
        undefined,
        false,
        escrowWantedToken,
        proposal,
        {
          wantedTokenMint: proposal.metadata.wantedTokenMint,
          tokenLedgerMint: proposal.metadata.wantedTokenMint,
          makerWantedTokenAccount: proposal.metadata.makerWantedTokenAccount,
          offerAddress: proposal.metadata.offer,
          offerTreasury: treasury,
          offeredTokenMint: proposal.metadata.offeredTokenMint,
        }
      );
      allInstructions.push({
        description: "Distributing profit",
        instructions: [
          distributeIx.createAccountIx,
          distributeIx.createInitAccountInstruction,
          distributeIx.distributeInstruction,
        ],
        partialSigner: distributeIx.tokenAccount,
      });
    }
    await sendTransactions(
      RPC_CONNECTION,
      wallet,
      allInstructions,
      SequenceType.Sequential
    );
  } catch (error) {
    console.log(error);
    throw error;
  }
}

export async function cancelSolseaProposal(
  realmName: string,
  proposal: PublicKey,
  offer: PublicKey,
  solseaEscrow: PublicKey,
  nftMint: PublicKey,
  wallet: AnchorWallet,
  treasury: PublicKey,
  cancellationReason: ICancellationReasonDto
) {
  try {
    const clubProgram = programFactory();
    const [realmAddress] = await PublicKey.findProgramAddress(
      [governanceSeed, Buffer.from(realmName)],
      splGovernanceProgram
    );
    const [clubData] = await PublicKey.findProgramAddress(
      [unqClubSeed, realmAddress.toBuffer()],
      clubProgram.programId
    );
    const [solseaAuthority] = await PublicKey.findProgramAddress(
      [solseaEscrow.toBuffer()],
      solseaProgram
    );
    const [vaultAddress] = await PublicKey.findProgramAddress(
      [
        unqClubSeed,
        clubData.toBuffer(),
        treasury.toBuffer(),
        vaultSeed,
        Buffer.from(CHAIN_ID),
      ],
      clubProgram.programId
    );

    const [memberAddress] = await PublicKey.findProgramAddress(
      [
        unqClubSeed,
        clubData.toBuffer(),
        unqClubMemberSeed,
        wallet.publicKey.toBuffer(),
      ],
      clubProgram.programId
    );

    const vaultTa = (
      await RPC_CONNECTION.getParsedTokenAccountsByOwner(vaultAddress, {
        mint: nftMint,
      })
    ).value[0].pubkey;

    const solseaEscrowTokens =
      await RPC_CONNECTION.getParsedTokenAccountsByOwner(solseaAuthority, {
        mint: nftMint,
      });

    if (!solseaEscrowTokens || !vaultTa) {
      throw new Error("Token account deleted");
    }

    const cancelSolseaIx = clubProgram.instruction.cancelSolseaOffer(CHAIN_ID, {
      accounts: {
        clubData,
        memberData: memberAddress,
        offer: offer,
        proposal: proposal,
        solseaAuthority,
        offeredTokenMint: nftMint,
        solseaEscrow,
        makerOfferedTokens: vaultTa,
        treasury,
        vault: vaultAddress,
        solseaEscrowTokens: solseaEscrowTokens.value[0].pubkey,
        payer: wallet.publicKey,
        escrowProgram: ESCRROW_PROGRAM,
        solseaProgram,
        systemProgram: SystemProgram.programId,
        tokenProgram: TOKEN_PROGRAM_ID,
      },
    });
    const tx = new Transaction({
      feePayer: wallet.publicKey,
      recentBlockhash: (await RPC_CONNECTION.getLatestBlockhash()).blockhash,
    });
    tx.add(cancelSolseaIx);
    const sigendTx = await wallet.signTransaction(tx);
    const serializedTx = sigendTx.serialize();
    await saveCancellationReason({
      ...cancellationReason,
      signedTransaction: encode(serializedTx),
    });
    await sendTransaction({
      connection: RPC_CONNECTION,
      transaction: sigendTx,
      wallet,
    });
  } catch (error) {
    console.log(error);
    throw error;
  }
}

export const getMintFromSolseaUrl = (solseaUrl: string) => {
  return solseaUrl.split("/")[4];
};
